import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface ImageItem {
    id: string;
    name: string;
    airRank: string;
    driveUrl: string;
    directUrl: string;
    createdAt: string;
}

interface Props {
    landingPageId: string;
    apiBase: string;
}

// Default topper data — users can override
const DEFAULT_NAMES = [
    'Shubhan Sengupta',
    'Mrinal Kishore',
    'Avika Aggarwal',
    'Aarav Aggarwal',
    'Rachit Sinha',
    'Saksham Agarwal',
    'Naveen Mittal',
    'Neev Mil Mankad',
    'Krishnamurty Siwal',
];
const DEFAULT_RANKS = [
    'AIR-1', 'AIR-4', 'AIR-5', 'AIR-10', 'AIR-16', 'AIR-46', 'AIR-49', 'AIR-58', 'AIR-60',
];

export default function ImageManager({ landingPageId, apiBase }: Props) {
    const [images, setImages] = useState<ImageItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState('');

    // Single image form
    const [form, setForm] = useState({ name: '', airRank: '', driveUrl: '' });
    const [saving, setSaving] = useState(false);

    // Bulk add state
    const [bulk, setBulk] = useState({
        baseUrl: '',
        prefix: '',
        ext: 'webp',
        start: 1,
        end: 9,
        names: DEFAULT_NAMES.join('\n'),
        ranks: DEFAULT_RANKS.join('\n'),
    });
    const [bulkSaving, setBulkSaving] = useState(false);

    useEffect(() => { fetchImages(); }, [landingPageId]);

    const fetchImages = async () => {
        setLoading(true);
        setError(null);
        try {
            const r = await axios.get(`${apiBase}/admin/images/${landingPageId}`);
            setImages(Array.isArray(r.data) ? r.data : []);
        } catch (e: any) {
            setError(e.response?.data?.error || e.message || 'Failed to load images');
        } finally {
            setLoading(false);
        }
    };

    const addOne = async (name: string, airRank: string, driveUrl: string) => {
        await axios.post(`${apiBase}/admin/images`, {
            landingPageId,
            name: name.trim(),
            airRank: airRank.trim(),
            driveUrl: driveUrl.trim(),
        });
    };

    const handleAddSingle = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.driveUrl) { setError('URL is required'); return; }
        if (!form.name)     { setError('Name is required'); return; }
        setSaving(true);
        setError(null);
        try {
            await addOne(form.name, form.airRank, form.driveUrl);
            setForm({ name: '', airRank: '', driveUrl: '' });
            setSuccess('Image added!');
            setTimeout(() => setSuccess(''), 2000);
            fetchImages();
        } catch (e: any) {
            setError(e.response?.data?.error || e.message || 'Failed to add');
        } finally {
            setSaving(false);
        }
    };

    const handleBulkAdd = async () => {
        if (!bulk.baseUrl) { setError('Base URL is required'); return; }
        setBulkSaving(true);
        setError(null);

        const names  = bulk.names.split('\n').map(s => s.trim()).filter(Boolean);
        const ranks  = bulk.ranks.split('\n').map(s => s.trim()).filter(Boolean);

        const baseUrl = bulk.baseUrl.replace(/\/$/, ''); // remove trailing slash
        const errors: string[] = [];

        for (let i = bulk.start; i <= bulk.end; i++) {
            const idx = i - bulk.start; // 0-based index into names/ranks arrays
            const filename = `${bulk.prefix}${i}.${bulk.ext}`;
            const url = `${baseUrl}/${filename}`;
            const name    = names[idx]  || `Topper ${i}`;
            const airRank = ranks[idx]  || '';
            try {
                await addOne(name, airRank, url);
            } catch (e: any) {
                errors.push(`#${i}: ${e.message}`);
            }
        }

        setBulkSaving(false);
        if (errors.length) {
            setError(`Some failed: ${errors.join(', ')}`);
        } else {
            const count = bulk.end - bulk.start + 1;
            setSuccess(`✓ Added ${count} images!`);
            setTimeout(() => setSuccess(''), 3000);
        }
        fetchImages();
    };

    const handleDelete = async (id: string, name: string) => {
        if (!window.confirm(`Remove "${name}"?`)) return;
        try {
            await axios.delete(`${apiBase}/admin/images/${id}`);
            setImages(images.filter(i => i.id !== id));
        } catch (e: any) {
            setError(e.response?.data?.error || 'Failed to delete');
        }
    };

    const handleDeleteAll = async () => {
        if (!window.confirm(`Delete all ${images.length} images for this page?`)) return;
        setError(null);
        for (const img of images) {
            try { await axios.delete(`${apiBase}/admin/images/${img.id}`); } catch {}
        }
        fetchImages();
    };

    return (
        <div className="image-manager">
            <div className="im-header">
                <h3>📸 Topper Images</h3>
                <p className="im-desc">
                    Images appear in the "10K+ Toppers" scroll on your landing page.
                </p>
            </div>

            {error && (
                <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
                    <span>⚠️ {error}</span>
                    <button onClick={() => setError(null)} className="alert-close">✕</button>
                </div>
            )}
            {success && (
                <div className="alert alert-success" style={{ marginBottom: '1rem' }}>✓ {success}</div>
            )}

            {/* ── BULK ADD ──────────────────────────────────────────────── */}
            <div className="im-section">
                <div className="im-section-header">
                    <h4>⚡ Bulk Add by URL Pattern</h4>
                    <span className="im-badge">Fastest</span>
                </div>
                <p className="im-desc" style={{ marginBottom: '1rem' }}>
                    If your images are named <strong>1.webp, 2.webp … 9.webp</strong> and hosted
                    on Cloudflare Pages or public Drive, just set the base URL and click Add All.
                </p>

                <div className="im-bulk-grid">
                    <div className="form-group" style={{ gridColumn: '1/-1' }}>
                        <label>Base URL <span className="required">*</span></label>
                        <input
                            type="url"
                            placeholder="https://ab-testing-platform.pages.dev"
                            value={bulk.baseUrl}
                            onChange={e => setBulk({ ...bulk, baseUrl: e.target.value })}
                        />
                        <div className="help-text">
                            URL of the folder. Images will be: <code>{(bulk.baseUrl || 'BASE').replace(/\/$/, '')}/{bulk.prefix || ''}{bulk.start}.{bulk.ext}</code>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>File prefix <span style={{fontWeight:'normal',color:'#9ca3af'}}>(leave blank if just numbers)</span></label>
                        <input
                            type="text"
                            placeholder="e.g., topper_ → topper_1.webp"
                            value={bulk.prefix}
                            onChange={e => setBulk({ ...bulk, prefix: e.target.value })}
                        />
                    </div>
                    <div className="form-group">
                        <label>File extension</label>
                        <select value={bulk.ext} onChange={e => setBulk({ ...bulk, ext: e.target.value })}>
                            <option value="webp">webp</option>
                            <option value="jpg">jpg</option>
                            <option value="jpeg">jpeg</option>
                            <option value="png">png</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Start #</label>
                        <input type="number" min={1} value={bulk.start}
                            onChange={e => setBulk({ ...bulk, start: parseInt(e.target.value) || 1 })} />
                    </div>
                    <div className="form-group">
                        <label>End #</label>
                        <input type="number" min={1} value={bulk.end}
                            onChange={e => setBulk({ ...bulk, end: parseInt(e.target.value) || 9 })} />
                    </div>

                    <div className="form-group" style={{ gridColumn: '1/3' }}>
                        <label>Student Names <span style={{fontWeight:'normal',color:'#9ca3af'}}>(one per line, in same order as images)</span></label>
                        <textarea
                            rows={5}
                            value={bulk.names}
                            onChange={e => setBulk({ ...bulk, names: e.target.value })}
                            placeholder="Avika Aggarwal&#10;Aarav Aggarwal&#10;Rachit Sinha..."
                            style={{ fontFamily: 'monospace', fontSize: '0.85rem', resize: 'vertical' }}
                        />
                    </div>
                    <div className="form-group" style={{ gridColumn: '3/5' }}>
                        <label>AIR Ranks <span style={{fontWeight:'normal',color:'#9ca3af'}}>(one per line)</span></label>
                        <textarea
                            rows={5}
                            value={bulk.ranks}
                            onChange={e => setBulk({ ...bulk, ranks: e.target.value })}
                            placeholder="AIR-5&#10;AIR-10&#10;AIR-16..."
                            style={{ fontFamily: 'monospace', fontSize: '0.85rem', resize: 'vertical' }}
                        />
                    </div>
                </div>

                <button
                    className="btn btn-primary"
                    onClick={handleBulkAdd}
                    disabled={bulkSaving || !bulk.baseUrl}
                    style={{ marginTop: '0.5rem' }}
                >
                    {bulkSaving
                        ? `Adding images…`
                        : `⚡ Add images ${bulk.start}–${bulk.end} (${bulk.end - bulk.start + 1} total)`}
                </button>
            </div>

            {/* ── DIVIDER ───────────────────────────────────────────────── */}
            <div className="im-divider"><span>or add a single image</span></div>

            {/* ── SINGLE ADD ────────────────────────────────────────────── */}
            <form className="im-add-form" onSubmit={handleAddSingle}>
                <h4>Add Single Image</h4>
                <div className="im-row">
                    <div className="form-group" style={{ flex: 2 }}>
                        <label>Image URL <span className="required">*</span></label>
                        <input
                            type="url"
                            placeholder="https://… OR Google Drive share link"
                            value={form.driveUrl}
                            onChange={e => setForm({ ...form, driveUrl: e.target.value })}
                        />
                        <div className="help-text">Direct URL or Google Drive "Anyone with link" share URL</div>
                    </div>
                    <div className="form-group" style={{ flex: 2 }}>
                        <label>Student Name <span className="required">*</span></label>
                        <input type="text" placeholder="e.g., Avika Aggarwal"
                            value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                        <label>AIR Rank</label>
                        <input type="text" placeholder="AIR-5"
                            value={form.airRank} onChange={e => setForm({ ...form, airRank: e.target.value })} />
                    </div>
                </div>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Adding…' : '+ Add Image'}
                </button>
            </form>

            {/* ── IMAGE LIST ────────────────────────────────────────────── */}
            <div className="im-list">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h4>Uploaded Images ({images.length})</h4>
                    {images.length > 0 && (
                        <button className="btn btn-danger-sm" onClick={handleDeleteAll} title="Delete all images for this page">
                            🗑 Delete All
                        </button>
                    )}
                </div>
                {loading ? (
                    <div className="loading">Loading…</div>
                ) : images.length === 0 ? (
                    <div className="empty-state" style={{ padding: '1.5rem', fontSize: '0.9rem' }}>
                        No images yet. Use Bulk Add above to add your 1.webp–9.webp images in one click.
                    </div>
                ) : (
                    <div className="im-cards">
                        {images.map((img, idx) => (
                            <div key={img.id} className="im-card">
                                <div className="im-num">{idx + 1}</div>
                                <div className="im-thumb">
                                    <img
                                        src={img.directUrl}
                                        alt={img.name}
                                        onError={e => {
                                            (e.target as HTMLImageElement).src =
                                                "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60'%3E%3Crect width='60' height='60' fill='%238b5cf6'/%3E%3Ctext x='50%25' y='55%25' text-anchor='middle' fill='white' font-size='22'%3E👤%3C/text%3E%3C/svg%3E";
                                        }}
                                    />
                                </div>
                                <div className="im-info">
                                    <strong>{img.name}</strong>
                                    {img.airRank && <span className="im-air">{img.airRank}</span>}
                                    <a href={img.directUrl} target="_blank" rel="noreferrer" className="im-link">
                                        🔗 View Image
                                    </a>
                                </div>
                                <button className="btn-danger-sm" onClick={() => handleDelete(img.id, img.name)} title="Remove">
                                    🗑
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
